import React, { Component } from "react";
import PropTypes from "prop-types";
import ReactCrop, { makeAspectCrop } from "react-image-crop";
import Measure from "react-measure";
import classNames from "classnames";

import Button from "components/library/Button";
import { CROP_NAMES } from "config/constants/editor";

import "react-image-crop/dist/ReactCrop.css";
import styles from "./ImageCropper.css";

export default class ImageCropper extends Component {
  constructor(props) {
    super(props);
    this.initialLoad = this._initialLoad.bind(this);
    this.handleResize = this._handleResize.bind(this);
    this.next = this._next.bind(this);

    const { image, cropRequirements } = props;
    const cropIds = Object.keys(image.crops || {}).filter(crop =>
      Object.keys(cropRequirements).includes(crop)
    );
    const aspects = cropIds.reduce((accum, cropId) => {
      return {
        ...accum,
        [cropId]: ((x, y) => x / y)(...image.crops[cropId].resize),
      };
    }, {});

    this.state = {
      cropIds,
      aspects,
      cropProgress: 0,
      crops: {},
      placeholder: true,
    };
  }

  componentDidMount() {
    try {
      this.props.imageValidator(this.props.image, this.props.imageRequirements);
    } catch (e) {
      this.props.setInvalid(e.message);
      return;
    }

    if (this.props.skip) {
      this.props.nextStep();
    }
  }

  _initialLoad(loadedImage) {
    // This is a bad place for this validation and skipping because it needs the image to load before validating.
    this.props.setLoadingState(false);

    const originalAspect = loadedImage.width / loadedImage.height;
    this.setState({
      originalAspect,
      natural: {
        height: loadedImage.naturalHeight,
        width: loadedImage.naturalHeight,
      },
      crops: {
        ...this.state.cropIds.reduce((accum, id) => {
          const aspectPercentageCrop = makeAspectCrop(
            {
              x: 0,
              y: 0,
              aspect: this.state.aspects[id],
              [this.state.aspects[id] > originalAspect
                ? "width"
                : "height"]: 100,
            },
            originalAspect
          );

          return {
            ...accum,
            [id]: {
              ...aspectPercentageCrop,
              pixelCrop: {
                x: aspectPercentageCrop.x * 0.01 * loadedImage.naturalWidth,
                y: aspectPercentageCrop.y * 0.01 * loadedImage.naturalHeight,
                width:
                  aspectPercentageCrop.width * 0.01 * loadedImage.naturalWidth,
                height:
                  aspectPercentageCrop.height *
                  0.01 *
                  loadedImage.naturalHeight,
              },
              minHeight: this.props.cropRequirements[id].minHeight
                ? (this.props.cropRequirements[id].minHeight /
                    loadedImage.naturalHeight) *
                  100
                : undefined,
              minWidth: this.props.cropRequirements[id].minWidth
                ? (this.props.cropRequirements[id].minWidth /
                    loadedImage.naturalWidth) *
                  100
                : undefined,
            },
          };
        }, {}),
      },
      placeholder: false,
    });
  }

  _handleResize(contentRect) {
    const { originalAspect } = this.state;
    const currentAspect = contentRect.scroll.width / contentRect.scroll.height;

    if (!this.state.initialHeight && contentRect.scroll.height !== 0) {
      this.setState({ initialHeight: contentRect.scroll.height });
    }

    if (originalAspect) {
      if (Math.abs(originalAspect - currentAspect) > 0.001) {
        // If aspect ratio is not approximately correct
        if (
          originalAspect / contentRect.scroll.width <
          contentRect.scroll.height
        ) {
          this.setState({
            cropAreaBounds: {
              height: Math.min(
                contentRect.scroll.width / originalAspect,
                this.state.natural.height
              ),
            },
          });
        }
      }
    }
  }

  _next(event) {
    event.preventDefault();

    if (this.state.cropProgress + 1 >= this.state.cropIds.length) {
      const crops = Object.entries(this.state.crops).reduce(
        (accum, [key, value]) => ({
          ...accum,
          [key]: {
            ...this.props.image.crops[key],
            autocrop: false,
            crop: {
              "top-left": [
                Math.round(value.pixelCrop.x),
                Math.round(value.pixelCrop.y),
              ],
              "bottom-right": [
                Math.round(value.pixelCrop.x + value.pixelCrop.width),
                Math.round(value.pixelCrop.y + value.pixelCrop.height),
              ],
            },
          },
        }),
        {}
      );
      this.props.setLoadingState(true);
      this.props.setCrops(crops).then(() => {
        this.props.setLoadingState(false);
        this.props.nextStep();
      });
    } else {
      this.setState({ cropProgress: this.state.cropProgress + 1 });
    }
  }

  render() {
    const { image } = this.props;
    const currentCropId = this.state.cropIds[this.state.cropProgress];
    const currentCrop = this.state.crops[currentCropId];

    return (
      <div className={styles.root}>
        <div className={styles.meta}>
          <h1>Crop your image</h1>
          {image.crops && !this.props.hideCropNames ? (
            <p>
              {this.state.cropProgress + 1}/{this.state.cropIds.length} –{" "}
              {CROP_NAMES[currentCropId]}
            </p>
          ) : null}
        </div>
        <div
          className={classNames(styles.placeholder, {
            [styles.hidden]: !this.state.placeholder,
          })}
        />
        <Measure scroll onResize={this.handleResize}>
          {({ measureRef }) => (
            <div
              ref={measureRef}
              className={classNames(styles.cropSizer, {
                [styles.hidden]: this.state.placeholder,
              })}
              style={{
                maxHeight: this.state.initialHeight,
                ...this.state.cropAreaBounds,
              }}
            >
              <ReactCrop
                src={image.base_image_url || ""}
                crop={currentCrop}
                minWidth={currentCrop && currentCrop.minWidth}
                minHeight={currentCrop && currentCrop.minHeight}
                onImageLoaded={this.initialLoad}
                onChange={(crop, pixelCrop) => {
                  this.setState({
                    crops: {
                      ...this.state.crops,
                      [currentCropId]: { ...crop, pixelCrop },
                    },
                  });
                }}
              />
            </div>
          )}
        </Measure>
        <div className={styles.buttonGroup}>
          <Button type="submit" onClick={this.next}>
            Next
          </Button>
          <Button
            role="link"
            onClick={() => this.props.history.go(-1)}
            type="cancel"
            aria-label="Cancel image uploading/editing and go back to the text editor"
          >
            Cancel
          </Button>
        </div>
      </div>
    );
  }
}

ImageCropper.propTypes = {
  history: PropTypes.shape({
    go: PropTypes.func.isRequired,
    push: PropTypes.func.isRequired,
  }),
  image: PropTypes.shape({
    crop: PropTypes.objectOf({
      resize: PropTypes.arrayOf(PropTypes.number).isRequired,
    }),
  }).isRequired,
  setCrops: PropTypes.func.isRequired,
  nextStep: PropTypes.func.isRequired,
  submitImage: PropTypes.func,
  imageValidator: PropTypes.func,
  imageRequirements: PropTypes.shape({
    minHeight: PropTypes.number,
    minWidth: PropTypes.number,
  }),
  setLoadingState: PropTypes.func.isRequired,
  hideCropNames: PropTypes.bool,
};
